import React, { useEffect, useRef, useState } from 'react';
import FadeIn from 'react-fade-in/lib/FadeIn';
import { LockClosedIcon, SearchCircleIcon } from '@heroicons/react/solid';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Landingfooter from './Landingfooter';
import Toast from './Toast';

function Signup() {
  const emailRef = useRef();

  const passwordConfirmRef = useRef();
  const passwordRef = useRef();
  const { currentUser, signup } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const passCheck = new RegExp(
    '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$'
  );

  async function handleSubmit(e) {
    e.preventDefault();

    if (!passCheck.test(passwordRef.current.value)) {
      setErrorMessage('Password does not meet security requirements.');
      setShowErrorToast(true);
      return setError('Password Requirements not met!');
    }
    if (passwordRef.current.value !== passwordConfirmRef.current.value) {
      setErrorMessage('Passwords do not match.');
      setShowErrorToast(true);
      return setError('Passwords do not match');
    }

    try {
      setLoading(true);
      await signup(emailRef.current.value, passwordRef.current.value);
      navigate('/create-profile');
    } catch (err) {
      setErrorMessage(err.message || 'Unable to create account. Please try again.');
      setShowErrorToast(true);
      setError(err);
      setLoading(false);
    }
  }

  if (!currentUser) {
    return (
      <>
        <FadeIn>
          <div className="min-h-screen flex items-center justify-center py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-red-50 relative">
            {/* Maple Leaf Background Watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              <svg className="w-[400px] h-[400px] md:w-[600px] md:h-[600px]" viewBox="0 0 32 32" fill="#fca5a5" opacity="0.6">
                <path d="M16.361,31h-0.72v-6.592l-7.596,0.949c-0.124,0.015-0.245-0.034-0.323-0.129c-0.079-0.096-0.103-0.225-0.063-0.343l0.917-2.753l-7.792-5.845c-0.107-0.081-0.161-0.213-0.14-0.346s0.115-0.242,0.242-0.284l2.606-0.869L1.678,11.16c-0.063-0.126-0.047-0.277,0.041-0.386s0.23-0.16,0.368-0.124l3.677,0.919l0.895-2.684c0.039-0.119,0.137-0.208,0.259-0.237C7.039,8.62,7.167,8.656,7.255,8.744l4.126,4.125L9.649,5.078c-0.028-0.126,0.014-0.259,0.11-0.345c0.097-0.087,0.234-0.115,0.355-0.074l2.727,0.909l2.852-4.752c0.13-0.217,0.487-0.217,0.617,0l2.852,4.752l2.727-0.909c0.126-0.041,0.259-0.013,0.355,0.074c0.097,0.086,0.139,0.219,0.11,0.345l-1.731,7.793l4.125-4.125c0.088-0.088,0.215-0.125,0.338-0.096c0.121,0.029,0.219,0.118,0.259,0.237l0.895,2.684l3.677-0.919c0.133-0.033,0.28,0.014,0.368,0.124c0.088,0.109,0.104,0.26,0.041,0.386L28.51,14.79l2.606,0.869c0.127,0.042,0.22,0.151,0.241,0.284c0.021,0.133-0.032,0.265-0.14,0.346l-7.792,5.844l0.918,2.754c0.039,0.118,0.015,0.247-0.063,0.343c-0.077,0.096-0.192,0.146-0.323,0.129l-7.596-0.949V31z"/>
              </svg>
            </div>

            <div className="max-w-md w-full space-y-8 bg-white/70 backdrop-blur-sm p-8 rounded-xl shadow-2xl relative z-10">
              <div>
                <Link to="/" className="flex items-center justify-center" aria-label="CanadaJobLeads home">
                  <SearchCircleIcon className="text-primary mr-2 w-10 h-10" />
                  <h1 className="text-center font-extrabold font-[Poppins] text-2xl md:text-3xl text-primary my-2 ">
                    Canada Job Leads
                  </h1>
                </Link>

                <h2 className="mt-6 text-center text-xl md:text-2xl font-bold text-gray-900">
                  Create Your Account
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link
                    to="/sign-in"
                    className="font-bold text-primary hover:text-red-900"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <input type="hidden" name="remember" defaultValue="true" />
                <div className="rounded-md shadow-sm -space-y-px">
                  <div>
                    <label htmlFor="email-address" className="sr-only">
                      Email
                    </label>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="rounded-t-md appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      placeholder="Email address"
                      ref={emailRef}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="sr-only">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900  focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      placeholder="Password"
                      ref={passwordRef}
                    />
                  </div>
                  <div>
                    <label htmlFor="password-confirm" className="sr-only">
                      Confirm Password
                    </label>
                    <input
                      id="password-confirm"
                      name="password-confirm"
                      type="password"
                      required
                      className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      placeholder="Confirm password"
                      ref={passwordConfirmRef}
                    />
                  </div>
                </div>
                <div className="text-sm px-3">
                  <p className="font-bold mb-1">Password Requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                    <li>At least one uppercase letter</li>
                    <li>At least one lowercase letter</li>
                    <li>At least one digit</li>
                    <li>At least one special character (#?!@$%^&*-)</li>
                    <li>Minimum 8 characters</li>
                  </ul>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      required
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                  </div>
                  <label
                    htmlFor="terms"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      className="font-bold text-primary hover:underline"
                    >
                      Terms & Conditions
                    </Link>
                  </label>
                </div>

                <div>
                  <button
                    disabled={loading}
                    type="submit"
                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-red-900 ease-in duration-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <LockClosedIcon
                        className="h-5 w-5 text-red-400 group-hover:text-red-200 ease-in duration-100"
                        aria-hidden="true"
                      />
                    </span>
                    Sign Up
                  </button>
                </div>
              </form>
            </div>
          </div>
          <Toast
            isOpen={showErrorToast}
            onClose={() => setShowErrorToast(false)}
            message={errorMessage}
            type="error"
          />
        </FadeIn>
        <Landingfooter />
      </>
    );
  }

  return <Navigate to="/dashboard" replace />;
}

export default Signup;
